import { Fragment } from 'react';
import { ReplyIcon } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import CollapsibleEmailMessage from './Message/CollapsibleEmailMessage';

// Component to hide accordion arrow
const NullIcon = () => null;

export default function EmailThreadList({
  threads,
  fullname,
  profile_picture_url,
  defaultPic,
  getOwnerDetails,
  setHoveredMessageId,
  hoveredMessageId,
  owners,
  onReplyToThread,
}) {
  if (threads.length === 0) {
    return null;
  }

  return (
    <>
      <Accordion type='multiple' className='m-3 space-y-3'>
        {threads.map((thread) => {
          // Defensive check: ensure thread.messages exists and is an array
          const threadMessages = Array.isArray(thread.messages)
            ? thread.messages
            : [];

          // Get unique sender avatars for thread header
          const uniqueSenders = Array.from(
            new Map(
              threadMessages
                .filter((msg) => msg.direction === 'received')
                .map((msg) => [
                  msg.sender_email,
                  {
                    name: msg.sender_name || fullname,
                    email: msg.sender_email,
                    avatar: profile_picture_url,
                  },
                ])
            ).values()
          );

          const previewText = thread.content_text;

          // Format timestamp
          const formattedTimestamp = thread.latest_timestamp
            ? new Date(thread.latest_timestamp).toLocaleString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                month: 'short',
                day: 'numeric',
                weekday: 'short',
              })
            : '';

          return (
            <Fragment key={thread.thread_id}>
              <AccordionItem
                value={thread.thread_id}
                className='rounded-lg border-none bg-card'
              >
                <AccordionTrigger
                  className='h-12 rounded-lg bg-secondary/50 px-3 py-2 hover:bg-secondary hover:no-underline data-[state=open]:rounded-b-none'
                  openIcon={NullIcon}
                  closeIcon={NullIcon}
                >
                  <div className='flex w-full min-w-0 items-center gap-3'>
                    {/* Sender Avatars - Overlapping */}
                    <div className='flex shrink-0 -space-x-2'>
                      {uniqueSenders.slice(0, 2).map((sender, idx) => (
                        <Avatar
                          key={idx}
                          className='h-8 w-8 border-2 border-background'
                        >
                          {sender.avatar ? (
                            <AvatarImage src={sender.avatar} />
                          ) : (
                            <AvatarFallback className='bg-muted text-[8px] text-foreground'>
                              {sender.name?.charAt(0)?.toUpperCase() || '?'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                      ))}
                      {uniqueSenders.length > 2 && (
                        <div className='flex h-10 w-10 items-center justify-center rounded-full border-2 border-background bg-muted text-xs text-foreground'>
                          +{uniqueSenders.length - 2}
                        </div>
                      )}
                    </div>

                    {/* Thread Content */}
                    <div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden'>
                      <span className='shrink-0 truncate text-sm font-semibold text-foreground'>
                        {thread.subject || 'No Subject'}
                      </span>
                      <span className='w-52 min-w-0 flex-1 truncate text-left text-sm text-muted-foreground'>
                        {previewText}
                      </span>
                    </div>

                    {/* Message Count Badge */}
                    {thread.message_count > 0 && (
                      <Badge
                        variant='secondary'
                        className='shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-normal text-foreground'
                      >
                        {thread.message_count}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className='rounded-b-lg bg-secondary/50 px-0 pb-4'>
                  <div className='px-3 pt-2'>
                    {/* Messages within thread */}
                    {threadMessages.map((message, msgIndex) => {
                      const isLastMessage =
                        msgIndex === threadMessages.length - 1;

                      // Map email message fields to expected structure
                      const mappedMessage = {
                        ...message,
                        text: message.content_text || message.content || '',
                        content_html: message.content,
                        message_type:
                          message.attachments?.length > 0 ? 'image' : 'text',
                        channel: 'email',
                      };

                      const senderName =
                        message.direction === 'sent'
                          ? getOwnerDetails(
                              message.user_id,
                              'fullname',
                              'email'
                            )
                          : message.sender_name || fullname;

                      const senderEmail = message.sender_email;

                      const formattedTimestamp = new Date(
                        message.timestamp
                      ).toLocaleString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        month: 'short',
                        day: 'numeric',
                        weekday: 'short',
                      });

                      // Get preview text for collapsed state
                      const previewMessageText = (
                        message.content_text ||
                        message.content ||
                        ''
                      )
                        .replace(/<[^>]*>/g, '')
                        .substring(0, 100);

                      return (
                        <CollapsibleEmailMessage
                          key={message._id}
                          message={message}
                          mappedMessage={mappedMessage}
                          senderName={senderName}
                          senderEmail={senderEmail}
                          formattedTimestamp={formattedTimestamp}
                          previewText={previewMessageText}
                          isLastMessage={isLastMessage}
                          profile_picture_url={profile_picture_url}
                          defaultPic={defaultPic}
                          fullname={fullname}
                          getOwnerDetails={getOwnerDetails}
                          setHoveredMessageId={setHoveredMessageId}
                        />
                      );
                    })}

                    {/* Reply Button at the bottom right */}
                    <div className='flex justify-end px-3 pb-2 pt-4'>
                      <Button
                        variant='default'
                        size='xs'
                        onClick={() =>
                          onReplyToThread(
                            thread.thread_id,
                            thread.subject,
                            previewText
                          )
                        }
                      >
                        <ReplyIcon className='mr-2 h-4 w-4' />
                        Reply to thread
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              {/* Timestamp below accordion item */}
              {formattedTimestamp && (
                <div className='flex justify-end py-1'>
                  <span className='text-xs text-muted-foreground'>
                    {formattedTimestamp}
                  </span>
                </div>
              )}
            </Fragment>
          );
        })}
      </Accordion>
    </>
  );
}
